import { Field } from '@qr/ui';
import type { TimestampLike } from '@qr/shared/types';

/**
 * Programacion del codigo: cuando empieza, cuando termina y cuantos usos
 * admite.
 *
 * Los campos de fecha del navegador trabajan en hora local y el servidor
 * guarda instantes absolutos, asi que la conversion se hace aqui, en un solo
 * sitio. Hacerla en cada punto de uso es como acaban las campanas terminando
 * cinco horas antes de lo que su dueno creia.
 */

export interface ScheduleValue {
  readonly from: string | null;
  readonly to: string | null;
  readonly scanCap: number | null;
}

interface ScheduleProps {
  readonly value: ScheduleValue;
  readonly scanCount: number;
  readonly disabled?: boolean;
  readonly onChange: (value: ScheduleValue) => void;
}

/** ISO 8601 -> el formato que espera `datetime-local`, en hora local. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** Lo que escribe el usuario (hora local) -> ISO absoluto para el servidor. */
function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Convierte a ISO lo que venga de Firestore, sea Timestamp o fecha. */
export function timestampToIso(value: unknown): string | null {
  if (!value) return null;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as TimestampLike).toDate().toISOString();
  }
  if (value instanceof Date) return value.toISOString();
  return null;
}

export function Schedule({ value, scanCount, disabled = false, onChange }: ScheduleProps) {
  const capReached =
    value.scanCap !== null && value.scanCap > 0 && scanCount >= value.scanCap;

  const endsBeforeItStarts =
    value.from !== null &&
    value.to !== null &&
    new Date(value.to).getTime() <= new Date(value.from).getTime();

  return (
    <div className="stack-sm">
      <div className="schedule">
        <Field
          label="Empieza a funcionar"
          type="datetime-local"
          value={toLocalInput(value.from)}
          disabled={disabled}
          hint="Déjalo vacío para que funcione desde ya."
          onChange={(e) => onChange({ ...value, from: fromLocalInput(e.target.value) })}
        />
        <Field
          label="Deja de funcionar"
          type="datetime-local"
          value={toLocalInput(value.to)}
          disabled={disabled}
          hint="Déjalo vacío para que no caduque."
          error={endsBeforeItStarts ? 'La fecha de fin debe ser posterior al inicio.' : null}
          onChange={(e) => onChange({ ...value, to: fromLocalInput(e.target.value) })}
        />
      </div>

      <Field
        label="Tope de escaneos"
        type="number"
        min={1}
        value={value.scanCap ?? ''}
        disabled={disabled}
        placeholder="Sin tope"
        hint={
          capReached
            ? undefined
            : `Lleva ${scanCount.toLocaleString('es')} escaneos. Al llegar al tope deja de redirigir.`
        }
        error={
          capReached
            ? `Ya se alcanzo el tope (${scanCount.toLocaleString('es')} escaneos). El codigo no redirige.`
            : null
        }
        onChange={(e) => {
          const parsed = Number.parseInt(e.target.value, 10);
          onChange({
            ...value,
            scanCap: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
          });
        }}
      />

      <p className="muted small">
        Cuando un código esta fuera de plazo o agotado sigue siendo válido: quien lo
        escanee verá un mensaje explicandolo, en vez de un error.
      </p>
    </div>
  );
}
